// ОТБИВКА МЕЖДУ КАРТОЧКАМИ — две редакции, их копи и геометрия. B5-5.
//
// ИСТОЧНИК — `beat()` борда `Life Map - s3 Deck v8.html` (`:584–610`). Отбивка накрывает экран
// целиком на пару секунд между карточкой и следующей: человек видит, куда легла точка, которую
// он только что поставил.
//
// ⭐ РЕДАКЦИЙ ДВЕ, И РАЗВИЛКА НЕ «ПЕРВАЯ КАРТОЧКА», А «ЕСТЬ ЛИ ЧТО СОЕДИНЯТЬ». Борд выбирает по
// `pts.length < 2` — то есть по числу карточек С ТОЧКОЙ, а не по номеру текущей. Разница видна
// на человеке, который пропустил первую карточку и собрал вторую: карточка не первая, а линии
// по-прежнему нет, и рисовать «линию так далеко» было бы рисовать одну точку и называть её
// линией. Развилка по номеру дала бы ровно это.
//
// ⛔ ДВЕ СТРОКИ `the line so far` — РАЗНЫЕ, И СВОДИТЬ ИХ ЗАПРЕЩЕНО (`s516.d6`). Одна здесь,
// капшн отбивки; вторая — шапка спарка (`deckSpark.sparkHead`). У шапки спарка есть условный
// хвост ` · {K} partial`, у капшна отбивки его нет вовсе. Общий зачин — не повод объявить их
// одной строкой: спарк говорит про состояние прохода, отбивка — про только что закрытую
// карточку. Отрицательное утверждение про хвост стоит в тесте.
//
// ⚠️ ТАЙМЕРЫ ИНЖЕКТИРУЕМЫ, И ЭТО ТРЕБОВАНИЕ ПРИЁМКИ, А НЕ УДОБСТВО. Тест, который ждёт 2200мс
// по-настоящему, платит две секунды за прогон и становится флаки на загруженной машине.

import { LAYERS } from '../../contracts.js';
import { cardFull, meanTouched } from './deckSpark.js';

/** Кадр отбивки — байт борда (`:584`). Он ШИРЕ и ВЫШЕ спарка: это экран, а не полоска. */
export const BEAT_BOX = { W: 300, H: 150, PT: 16, PB: 26 };

/**
 * Сколько держать каждую редакцию и сколько уходить (борд `:598`, `:610`).
 *
 * ⚠️ У ПЕРВОЙ ТОЧКИ ПАУЗА ДЛИННЕЕ, И ЭТО НЕ ОПЕЧАТКА БОРДА. У неё внутри своя анимация —
 * падение точки, ореол и подпись «сколько ещё поставить», которая появляется на 1.1с. Урежь до
 * 1750, и человек увидит подпись за шесть сотых секунды до того, как кадр начнёт уходить.
 */
export const BEAT_HOLD = { first: 2200, line: 1750 };

/** Уход кадра — снимается `.on`, через столько узлы удаляются (борд `:598`). */
export const BEAT_OUT = 320;

/** Горизонталь карточки в кадре отбивки (борд `:585`). Поля шире, чем у спарка: 8 против 6. */
export const beatX = (i, n, box = BEAT_BOX) => (
  n === 1 ? box.W / 2 : 8 + i * ((box.W - 16) / (n - 1))
);

/** Вертикаль значения в кадре отбивки (борд `:585`). */
export const beatY = (v, box = BEAT_BOX) => (
  box.PT + ((3 - v) / 6) * (box.H - box.PT - box.PB)
);

/** Точки в кадре отбивки — те же правила, что у спарка, но своя сетка. */
export function beatPoints(vals, box = BEAT_BOX) {
  const rows = Array.isArray(vals) ? vals : [];
  const n = rows.length;
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const m = meanTouched(rows[i]);
    if (m === null) continue;
    out.push({ i, x: beatX(i, n, box), y: beatY(m, box) });
  }
  return out;
}

/**
 * Что показывает отбивка после карточки `prev`.
 *
 * Возвращает РЕДАКЦИЮ и всё, что ей нужно, — но ни одной строки разметки: рисует экран. Здесь
 * же выбирается, какой сегмент «растёт» (последний, ведущий в только что закрытую карточку) и
 * какая точка «выскакивает».
 *
 * @param {Array<Array<number|null>>} vals ряды ползунков
 * @param {number} prev индекс только что закрытой карточки
 * @param {{W:number,H:number,PT:number,PB:number}} [box]
 */
export function beatFrame(vals, prev, box = BEAT_BOX) {
  const rows = Array.isArray(vals) ? vals : [];
  const n = rows.length;
  const points = beatPoints(rows, box);
  const done = rows.filter((row) => cardFull(row)).length;

  // Ось: пунктирная горизонталь по нулю плюс вертикаль на каждую карточку (борд `:588`).
  const axis = {
    baselineY: beatY(0, box),
    ticks: rows.map((_, i) => beatX(i, n, box)),
  };

  if (points.length < 2) {
    // Точки может не быть вовсе (человек ушёл с нетронутой карточки) — тогда она садится на ось.
    const p = points[0] ?? { i: prev, x: beatX(prev, n, box), y: beatY(0, box) };
    return {
      kind: 'first', hold: BEAT_HOLD.first, axis, point: p, remaining: Math.max(0, n - 1), done, total: n,
    };
  }

  const segments = [];
  for (let k = 1; k < points.length; k += 1) {
    const q = points[k - 1];
    const p = points[k];
    const mx = (q.x + p.x) / 2;
    segments.push({
      d: `M${q.x} ${q.y} C${mx} ${q.y} ${mx} ${p.y} ${p.x} ${p.y}`,
      gap: p.i - q.i > 1,
      // «Растёт» сегмент, ВЕДУЩИЙ В закрытую карточку: он и есть то, что человек только что сделал.
      grow: p.i === prev,
    });
  }

  return {
    kind: 'line', hold: BEAT_HOLD.line, axis, points, segments, prev, done, total: n,
  };
}

/**
 * Копи отбивки (борд `:595–597`, `:606–609`). Собирается из кадра, а не хранится строками с
 * подставленными числами: число этапов и число собранных карточек приходят из конверта.
 *
 * `ahead` есть только у первой редакции — это подпись справа от одинокой точки.
 */
export const BEAT_EYEBROW = (roman) => `card ${roman} · read`;

export const beatAhead = (remaining) => `${remaining} MORE TO PLACE`;

export function beatCaption(frame) {
  return frame.kind === 'first'
    ? { lead: 'the first point of your line · ', bold: `${frame.done}`, trail: ` / ${frame.total}` }
    : { lead: 'the line so far · ', bold: `${frame.done}`, trail: ` / ${frame.total} stages` };
}

/** Число слоёв — из контракта, чтобы «семь» не завелось здесь третьей копией. */
export const LAYER_COUNT = LAYERS.length;
